import { Component, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs';
import { AuthService } from './infrastructure/auth/auth.service';
import { hayRegresoDeMicrosoftPendiente } from './infrastructure/entra/cliente';
import { BloqueoRecargaService } from './application/bloqueo-recarga';

/**
 * Cuanto tiempo de señal continua exigimos antes de dejar que la app se
 * recargue sola. No basta con "hay señal en este instante" — en campo la
 * señal puede prenderse y apagarse en segundos (un wifi débil que "conecta"
 * pero no sostiene). Si se corta antes de cumplir el minimo, el conteo
 * arranca de cero la proxima vez que vuelva.
 *
 * Bajado de 5 a 2, y despues a 1 minuto (2026-10-23) -- 5 minutos corridos de
 * señal estable rara vez se cumplian en campo con wifi intermitente, dejando
 * inspectores atascados en versiones viejas por dias. El flujo real del
 * inspector sube cada foto al instante (ver subirYGuardarAvance en
 * calidad.ts), asi que casi nunca hay trabajo sin guardar esperando una
 * ventana larga -- 1 minuto sigue dando margen contra un corte de señal muy
 * breve (ver docs/hallazgo-recarga-offline-504...), y es mucho mas
 * alcanzable en una jornada real que 2 o 5.
 */
const MINUTOS_SENAL_ESTABLE_REQUERIDOS = 1;
const MS_SENAL_ESTABLE_REQUERIDOS = MINUTOS_SENAL_ESTABLE_REQUERIDOS * 60_000;
/** Cada cuanto reintentamos mientras hay una recarga pendiente esperando condiciones seguras. */
const MS_ENTRE_INTENTOS = 5_000;
/** Margen minimo entre dos checkForUpdate() disparados por volver a primer plano -- evita golpear la red si el usuario cambia de app repetidamente en poco tiempo. */
const MS_MINIMO_ENTRE_CHEQUEOS_AL_VOLVER = 60_000;

/** Shell raíz de la aplicación. */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit {
  private readonly auth = inject(AuthService)
  private readonly swUpdate = inject(SwUpdate, { optional: true })
  private readonly bloqueoRecarga = inject(BloqueoRecargaService)

  private recargaPendiente = false
  private intervaloRecarga?: ReturnType<typeof setInterval>
  /** Marca de tiempo desde que hay señal sin cortes, o null si no hay señal ahora mismo. */
  private desdeCuandoHaySenal: number | null = navigator.onLine ? Date.now() : null
  /** Marca de tiempo del último checkForUpdate() disparado por volver a primer plano, para no golpear la red en cada cambio de pestaña/app. */
  private ultimoChequeoAlVolver = 0

  ngOnInit(): void {
    void this.auth.inicializar()
    window.addEventListener('online', () => {
      this.desdeCuandoHaySenal = Date.now()
    })
    window.addEventListener('offline', () => {
      this.desdeCuandoHaySenal = null
    })
    this.vigilarServiceWorker()
    this.vigilarVueltaAPrimerPlano()
  }

  /**
   * En celular, la app casi nunca se "cierra" de verdad — el inspector la
   * pone en segundo plano entre una inspección y otra, y la retoma horas
   * después. Sin esto, la única forma de detectar una versión nueva era
   * esperar el ciclo de `MS_ENTRE_INTENTOS`, sin ningún disparador ligado al
   * momento real en que el inspector vuelve a usar la app (2026-10-23).
   */
  private vigilarVueltaAPrimerPlano(): void {
    if (!this.swUpdate?.isEnabled) return
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      const ahora = Date.now()
      if (ahora - this.ultimoChequeoAlVolver < MS_MINIMO_ENTRE_CHEQUEOS_AL_VOLVER) return
      this.ultimoChequeoAlVolver = ahora
      void this.swUpdate!.checkForUpdate()
    })
  }

  /**
   * Dos disparadores de recarga automatica: version nueva desplegada, o el
   * Service Worker cayendo en estado "unrecoverable" (por ejemplo tras
   * toparse con una respuesta de error cacheada al reintentar refrescar sus
   * assets sin red). Ambos casos convergen en el mismo mecanismo de espera
   * segura — ver programarRecarga().
   */
  private vigilarServiceWorker(): void {
    if (!this.swUpdate?.isEnabled) return

    this.swUpdate.unrecoverable.subscribe((evento) => {
      console.error('[sw] estado no recuperable, se recargará cuando sea seguro:', evento.reason)
      this.programarRecarga()
    })

    this.swUpdate.versionUpdates
      .pipe(filter((evento) => evento.type === 'VERSION_READY'))
      .subscribe(() => {
        // activateUpdate() no necesita red (ya se descargo todo antes de
        // avisar VERSION_READY) — el reload si dispara una navegacion real,
        // por eso el activate y el reload van por separado.
        void this.swUpdate!.activateUpdate().then(() => this.programarRecarga())
      })
  }

  /**
   * No recarga de inmediato — deja la intencion "pendiente" y reintenta cada
   * pocos segundos hasta que sea seguro. Puede quedar esperando minutos,
   * horas o dias si hace falta: nunca fuerza un reload mientras alguien
   * tenga una captura sin guardar, mientras la señal sea inestable, o
   * mientras un login de Microsoft este en proceso.
   */
  private programarRecarga(): void {
    if (this.recargaPendiente) return
    this.recargaPendiente = true
    this.intentarRecargarCuandoSeaSeguro()
    this.intervaloRecarga = setInterval(() => this.intentarRecargarCuandoSeaSeguro(), MS_ENTRE_INTENTOS)
  }

  private intentarRecargarCuandoSeaSeguro(): void {
    // Si justo estamos a mitad de procesar un regreso de loginRedirect de
    // Microsoft, un reload nuestro en este instante puede cortar ese
    // procesamiento antes de que MSAL termine de limpiar su marca interna
    // de "interaccion en curso" — dejandola pegada y bloqueando el proximo
    // intento de login con Microsoft indefinidamente.
    if (hayRegresoDeMicrosoftPendiente()) return

    // Fotos y campos sin guardar en captura-producto/nuevo-proveedor viven
    // solo en memoria — un reload en este momento los pierde sin remedio.
    if (this.bloqueoRecarga.hayTrabajoSinGuardar()) return

    // Señal estable sostenida, no un instante suelto — ver comentario arriba.
    if (this.desdeCuandoHaySenal === null) return
    if (Date.now() - this.desdeCuandoHaySenal < MS_SENAL_ESTABLE_REQUERIDOS) return

    if (this.intervaloRecarga) clearInterval(this.intervaloRecarga)
    window.location.reload()
  }
}
